import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ACCOUNT_DEACTIVATED_LOGIN_MESSAGE,
  consumeAccountDeactivatedLogout,
} from '../../utils/portalSession';
import Logo from '../../components/common/Logo/Logo';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import Text from '../../components/common/Text/Text';
import Link from '../../components/common/Link/Link';
import styles from './Login.module.css';

const Login: React.FC = () => {
  const { user, signIn, loading: authLoading, roles, defaultPath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as {
    successMessage?: string;
    accountDeactivated?: boolean;
  } | null;
  const successMessage = locationState?.successMessage;
  const [accountDeactivated] = useState(
    () => Boolean(locationState?.accountDeactivated) || consumeAccountDeactivatedLogout(),
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user && roles.length > 0) {
      navigate(defaultPath, { replace: true });
    }
  }, [authLoading, user, roles, defaultPath, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Preencha todos os campos.');
      return;
    }

    try {
      setLoading(true);
      await signIn(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['login-page']}>
      <div className={styles['login-card']}>
        <Logo className={styles['login-logo-wrapper']} />

        <form className={styles['login-form']} onSubmit={handleSubmit} noValidate>
          <Input
            id="email"
            label="Email"
            type="email"
            placeholder="Insira seu email aqui"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            disabled={loading}
          />

          <Input
            id="password"
            label="Senha"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
          />

          <Link href="#" className={styles['forgot-password']}>
            Esqueci minha senha
          </Link>

          {(error || accountDeactivated) && (
            <Text variant="error" as="p" className={styles['login-error']}>
              {error || ACCOUNT_DEACTIVATED_LOGIN_MESSAGE}
            </Text>
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={loading}
            className={styles['login-btn']}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>

          {successMessage && (
            <Text variant="body" align="center" as="p" className={styles['login-success']}>
              {successMessage}
            </Text>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
